import { createSlice } from '@reduxjs/toolkit';
import type { AxiosResponse } from 'axios';
import { normalize } from 'normalizr';
import storage from 'redux-persist/lib/storage';
import { persistReducer } from 'redux-persist';
import { api } from 'libs';
import schema from 'state/schema';
import { getComparedProductsCount } from 'state/selectors/products';
import serializeRequestError from 'state/utils/serialize-request-error';
import { filterSet } from './filters';
import type { AppDispatch, AppGetState, RequestResultAction } from './index';

const INITIAL_META = {
  currentPage: 1,
  hasMore: false,
  perPage: null as number | null,
  total: null as number | null,
};

const mergeById = (
  state: { byId: Record<string, unknown> },
  payload: { entities?: { products?: Record<string, unknown> } }
) => {
  state.byId = {
    ...state.byId,
    ...(payload.entities?.products || {}),
  };
};

const productsSlice = createSlice({
  name: 'lenderspotlight/products',
  initialState: {
    byId: {},
    staleCompared: 0,
    compareVisible: false,
    compared: [] as (string | number)[],
    error: null,
    meta: INITIAL_META,
    pendingRequestsCount: 0,
    results: [] as any[],
  },
  reducers: {
    compareAdded: (state, action) => {
      const productId = action.payload ?? (action as any).id;
      state.compared = [productId, ...state.compared];
    },
    addExclusiveStarted: (state) => {
      state.pendingRequestsCount += 1;
    },
    addExclusiveFailed: (state) => {
      state.pendingRequestsCount -= 1;
    },
    addExclusiveSucceeded: (state, action) => {
      mergeById(state, action.payload);
      state.pendingRequestsCount -= 1;
    },
    compareCleared: (state) => {
      state.compared = [];
      state.compareVisible = false;
    },
    compareHidden: (state) => {
      state.compareVisible = false;
    },
    compareShown: (state) => {
      state.compareVisible = true;
    },
    compareVisibilityToggled: (state) => {
      state.compareVisible = !state.compareVisible;
    },
    compareRemoved: (state, action) => {
      const productId = action.payload ?? (action as any).id;
      state.compared = state.compared.filter((id) => id !== productId);
    },
    compareReset: (state, action) => {
      state.compared = action.payload;
    },
    fetchStarted: (state) => {
      state.error = null;
      state.pendingRequestsCount += 1;
    },
    fetchByComparedSucceeded: (state, action) => {
      mergeById(state, action.payload);
      state.pendingRequestsCount -= 1;
    },
    fetchByIdSucceeded: (state, action) => {
      mergeById(state, action.payload);
      state.pendingRequestsCount -= 1;
    },
    fetchFailed: (state, action) => {
      state.error = action.payload ?? null;
      state.pendingRequestsCount -= 1;
    },
    fetchMoreSucceeded: (state, action) => {
      mergeById(state, action.payload);
      state.error = null;
      state.meta = action.payload.meta;
      state.results = [...state.results, ...action.payload.result];
      state.pendingRequestsCount -= 1;
    },
    fetchSucceeded: (state, action) => {
      mergeById(state, action.payload);
      state.error = null;
      state.meta = action.payload.meta;
      state.results = action.payload.result;
      state.pendingRequestsCount -= 1;
    },
    resultsCleared: (state) => {
      state.error = null;
      state.results = [];
      state.meta = INITIAL_META;
    },
    staleComparedReset: (state) => {
      state.staleCompared = 0;
    },
    staleComparedSet: (state, action) => {
      state.staleCompared = action.payload;
    },
    updateExclusiveStarted: (state) => {
      state.pendingRequestsCount += 1;
    },
    updateExclusiveFailed: (state) => {
      state.pendingRequestsCount -= 1;
    },
    updateExclusiveSucceeded: (state, action) => {
      mergeById(state, action.payload);
      state.pendingRequestsCount -= 1;
    },
  },
});

export default persistReducer({ key: 'products', whitelist: ['compared'], storage }, productsSlice.reducer);

const {
  compareAdded,
  addExclusiveFailed,
  addExclusiveStarted,
  addExclusiveSucceeded,
  compareCleared,
  compareRemoved,
  compareReset,
  compareHidden,
  compareShown,
  compareVisibilityToggled,
  fetchByComparedSucceeded,
  fetchByIdSucceeded,
  fetchFailed,
  fetchMoreSucceeded,
  fetchStarted,
  fetchSucceeded,
  resultsCleared,
  staleComparedReset,
  staleComparedSet,
  updateExclusiveFailed,
  updateExclusiveStarted,
  updateExclusiveSucceeded,
} = productsSlice.actions;

/**
 * Action Types
 * @type {Object}
 */
export const actionTypes = {
  compareAdded: compareAdded.type,
  addExclusiveFailed: addExclusiveFailed.type,
  addExclusiveSucceeded: addExclusiveSucceeded.type,
  compareRemoved: compareRemoved.type,
  updateExclusiveFailed: updateExclusiveFailed.type,
  updateExclusiveSucceeded: updateExclusiveSucceeded.type,
};

/**
 * Add Exclusive Success Action
 * @param {Object} response
 * @return {Object}
 */
const addExclusiveSuccess = (response: AxiosResponse) =>
  addExclusiveSucceeded(normalize(response.data, schema.product));

/**
 * Get the meta values from the response.
 * @param  {Object} headers
 * @return {Object}
 */
const getMeta = (headers: AxiosResponse['headers'] | null = null) =>
  headers === null
    ? {}
    : {
        currentPage: parseInt(headers['x-current-page'], 10),
        hasMore: headers['x-has-more'] === 'true' || false,
        perPage: parseInt(headers['x-per-page'], 10),
        total: parseInt(headers['x-total-count'], 10),
      };

/**
 * Fetch By Id Success Action
 * @param {Object} response
 * @return {Object}
 */
const fetchByIdSuccess = (response: AxiosResponse) => fetchByIdSucceeded(normalize(response.data, schema.product));

/**
 * Fetch More Products Success Action
 * @param {Object} response
 * @return {Object}
 */
const fetchMoreSuccess = (response: AxiosResponse) => {
  const payload = {
    ...normalize(response.data, [schema.product]),
    meta: getMeta(response.headers),
  };

  return fetchMoreSucceeded(payload);
};

/**
 * Fetch Products Success Action
 * @param {Object} response
 * @return {Object}
 */
const fetchSuccess = (response: AxiosResponse) => {
  const payload = {
    ...normalize(response.data, [schema.product]),
    meta: getMeta(response.headers),
  };

  return fetchSucceeded(payload);
};

const fetchError = (error: unknown) => fetchFailed(serializeRequestError(error));

const fetchByComparedSuccess = (response: AxiosResponse) =>
  fetchByComparedSucceeded(normalize(response.data, [schema.product]));

export const clearResults = () => resultsCleared();

/**
 * Update Exclusive Success Action
 * @param {Object} response
 * @return {Object}
 */
const updateExclusiveByIdSuccess = (response: AxiosResponse) =>
  updateExclusiveSucceeded(normalize(response.data, schema.product));

/**
 * Add Exclusive Product Thunk
 * @param {Number} brokerageId
 * @param {Object} payload
 * @return {Promise}
 */
const addExclusive =
  (brokerageId: string | number, payload: Record<string, unknown>) =>
  async (dispatch: AppDispatch): Promise<RequestResultAction> => {
    dispatch(addExclusiveStarted());

    try {
      const response = await api.post(`brokerages/${brokerageId}/products`, payload);
      return dispatch(addExclusiveSuccess(response));
    } catch (error) {
      return dispatch({ type: addExclusiveFailed.type, error: serializeRequestError(error) });
    }
  };

/**
 * Fetch Products Thunk
 * @return {Promise}
 */
export const fetch = (params = {}, lenders: (string | number)[] = []) => {
  const payload = lenders.length ? { ...params, lenders } : params;
  return async (dispatch: AppDispatch) => {
    dispatch(fetchStarted());
    dispatch(filterSet(params));

    try {
      const response = await api.get('products', { params: payload });
      return dispatch(fetchSuccess(response));
    } catch (error) {
      return dispatch(fetchError(error));
    }
  };
};

/**
 * Fetch by Id Products Thunk
 * @param {Number} productId
 * @return {Promise}
 */
export const fetchById = (productId: string | number) => async (dispatch: AppDispatch) => {
  dispatch(fetchStarted());

  try {
    const response = await api.get(`products/${productId}`);
    return dispatch(fetchByIdSuccess(response));
  } catch (error) {
    return dispatch(fetchError(error));
  }
};

/**
 * Fetch Products that are currently compared
 */
export const fetchByCompared = () => async (dispatch: AppDispatch, getState: AppGetState) => {
  const id = getState().products.compared;
  if (!id.length) return null;

  dispatch(fetchStarted());

  try {
    const response = await api.get('products', { params: { id } });
    dispatch(fetchByComparedSuccess(response));
    dispatch(staleComparedSet(id.length - response.data.length));
    return dispatch(compareReset(response.data.map((p: { id: number | string }) => p.id)));
  } catch (error) {
    return dispatch(fetchError(error));
  }
};

/**
 * Toggles visibility of compared sidebar.
 */
export const toggleCompareVisible = () => (dispatch: AppDispatch) => dispatch(compareVisibilityToggled());

/**
 * Fetch More Rates based on the current meta data.
 * @return {Function}
 */
export const fetchMore =
  (lenders: (string | number)[] = []) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    dispatch(fetchStarted());

    const params = {
      ...getState().filters.options,
      page: getState().products.meta.currentPage + 1,
    };
    const payload = lenders.length ? { ...params, lenders } : params;

    try {
      const response = await api.get('products', { params: payload });
      return dispatch(fetchMoreSuccess(response));
    } catch (error) {
      return dispatch(fetchError(error));
    }
  };

/**
 * @param {String} type
 */
const wrapComparedAction =
  (action: (id: string | number) => Parameters<AppDispatch>[0]) =>
  (id: string | number) =>
  (dispatch: AppDispatch, getState: AppGetState) => {
    const prevCount = getComparedProductsCount(getState());
    dispatch(action(id));
    const nextCount = getComparedProductsCount(getState());

    if (nextCount === 0) {
      dispatch(compareHidden());
    } else if (prevCount === 0) {
      dispatch(compareShown());
    }
  };

const addCompared = wrapComparedAction(
  (id) => (dispatch: AppDispatch, getState: AppGetState) => dispatch(compareAdded(id))
);

export const clearCompared = () => (dispatch: AppDispatch) => dispatch(compareCleared());

export const resetStaleCompared = () => (dispatch: AppDispatch) => {
  dispatch(staleComparedReset());
};

export const removeCompared = wrapComparedAction((id) => compareRemoved(id));

export const toggleCompared = (id: string | number) => (dispatch: AppDispatch, getState: AppGetState) =>
  getState().products.compared.includes(id) ? dispatch(removeCompared(id)) : dispatch(addCompared(id));

/**
 * Remove Product by ID Thunk.
 * @return {Promise}
 */
const updateExclusiveById =
  (brokerageId: string | number, productId: string | number, payload: Record<string, unknown>) =>
  async (dispatch: AppDispatch): Promise<RequestResultAction> => {
    dispatch(updateExclusiveStarted());

    try {
      const response = await api.put(`brokerages/${brokerageId}/products/${productId}`, payload);
      return dispatch(updateExclusiveByIdSuccess(response));
    } catch (error) {
      return dispatch({ type: updateExclusiveFailed.type, error: serializeRequestError(error) });
    }
  };

export const upsertExclusiveById = (
  brokerageId: string | number,
  productId: string | number | null | undefined = null,
  data: Record<string, unknown>
) => (productId ? updateExclusiveById(brokerageId, productId, data) : addExclusive(brokerageId, data));
